/**
 * IncubationEnvironmentCard — Live sensor display for the incubation room (ipac70)
 *
 * Shows temperature, target temp, power, mode, fan speed,
 * 24h sparkline, and last update timestamp.
 */

import { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { LineChart, Line, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts'
import { Thermometer, AlertTriangle, Wifi, WifiOff } from 'lucide-react'
import { useSensorData } from '@/hooks/useSensorData'
import { useDeviceCommand } from '@/hooks/useDeviceCommand'

function formatRelativeTime(date) {
  if (!date) return 'never'
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
  if (seconds < 60) return `${seconds}s ago`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`
  return `${Math.floor(seconds / 86400)}d ago`
}

const MODE_COLOURS = {
  cooling: 'bg-blue-50 text-blue-700 border-blue-200',
  heating: 'bg-orange-50 text-orange-700 border-orange-200',
  auto: 'bg-violet-50 text-violet-700 border-violet-200'
}

export default function IncubationEnvironmentCard() {
  const { latest, history, status, loading, lastUpdate } = useSensorData('incubation', 'ipac70')
  const { sendCommand, pendingCommand, isCommandPending } = useDeviceCommand('incubation', 'ipac70')

  const isStale = useMemo(() => {
    if (!lastUpdate) return true
    return (Date.now() - new Date(lastUpdate).getTime()) > 60_000
  }, [lastUpdate])

  const temperature = latest.temperature?.value ?? null
  const tempSet = latest.temp_set?.value ?? null

  const power = status.power?.value
  const isPowerOn = power === 'on' || power === '1' || power === 'true'
  const mode = status.mode?.value ?? null
  const fanSpeed = status.fan_speed?.value ?? null

  const sparklineData = useMemo(() => {
    if (!history || history.length === 0) return []
    const step = Math.max(1, Math.floor(history.length / 100))
    return history.filter((_, i) => i % step === 0).map(r => ({ v: r.value }))
  }, [history])

  const hasData = temperature !== null

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Thermometer className="h-4 w-4" />
            Room Environment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground text-sm">Loading...</div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
          <Thermometer className="h-4 w-4" />
          Room Environment
          {isStale && hasData && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs ml-auto">
              <AlertTriangle className="h-3 w-3 mr-1" />
              Stale
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!hasData ? (
          <div className="flex flex-col items-center justify-center text-muted-foreground text-sm gap-1 py-2">
            <WifiOff className="h-4 w-4" />
            <span>No sensor data</span>
          </div>
        ) : (
          <div className="space-y-2">
            {/* Temperature + Target */}
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-semibold tabular-nums">
                {temperature.toFixed(1)}
              </span>
              <span className="text-sm text-muted-foreground">°C</span>
              {tempSet !== null && (
                <span className="text-xs text-muted-foreground ml-auto">
                  / {tempSet.toFixed(1)}°C
                </span>
              )}
            </div>

            {/* Status badges */}
            <div className="flex flex-wrap gap-1">
              {power !== undefined && (
                <Badge variant="outline" className={`text-xs ${isPowerOn ? 'bg-green-50 text-green-700 border-green-200' : ''}`}>
                  {isPowerOn ? 'On' : 'Off'}
                </Badge>
              )}
              {mode && (
                <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={() => {
                        if (!isPowerOn || isCommandPending) return
                        const newMode = mode === 'heating' ? 'cooling' : 'heating'
                        sendCommand('mode', newMode)
                      }}
                      disabled={!isPowerOn || isCommandPending}
                      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors ${
                        !isPowerOn
                          ? 'bg-muted text-muted-foreground border-muted cursor-not-allowed opacity-50'
                          : isCommandPending && pendingCommand?.command_key === 'mode'
                            ? 'bg-amber-50 text-amber-700 border-amber-200 animate-pulse cursor-wait'
                            : `${MODE_COLOURS[mode] || ''} cursor-pointer hover:opacity-80`
                      }`}
                    >
                      {isCommandPending && pendingCommand?.command_key === 'mode'
                        ? `→ ${pendingCommand.value}`
                        : mode
                      }
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="text-xs max-w-48">
                    {!isPowerOn
                      ? 'Power is off'
                      : isCommandPending
                        ? 'Waiting for AC to respond...'
                        : `Click to switch to ${mode === 'heating' ? 'cooling' : 'heating'}`
                    }
                    <div className="text-muted-foreground mt-0.5">
                      Managed by thermostat (SP 20°C ±2°C)
                    </div>
                  </TooltipContent>
                </Tooltip>
                </TooltipProvider>
              )}
              {fanSpeed && (
                <Badge variant="outline" className="text-xs">
                  Fan: {fanSpeed}
                </Badge>
              )}
            </div>

            {/* Sparkline */}
            {sparklineData.length > 1 && (
              <div className="h-10">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={sparklineData}>
                    <ChartTooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null
                        return (
                          <div className="rounded-md bg-white px-2 py-1 text-xs shadow-md border">
                            <span className="font-medium tabular-nums">{payload[0].value.toFixed(1)}°C</span>
                          </div>
                        )
                      }}
                    />
                    <Line
                      type="monotone"
                      dataKey="v"
                      stroke="hsl(var(--foreground))"
                      strokeWidth={1.5}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Last update */}
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Wifi className="h-3 w-3" />
              <span>{formatRelativeTime(lastUpdate)}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
